import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, combineLatest, map, mergeMap, of, switchMap } from 'rxjs';
import { WearablesIntegrationService } from '@providers/api/wearables-integration/wearables-integration';
import { TrackerSettingsActions } from '../actions';
import { TrackerService } from '../tracker.service';
import { UserHealthCategorySettings } from '../tracker.state.interface';

/**
 * @description Tracker Settings Effects.
 * @exports
 * @class TrackerSettingsEffects
 */
@Injectable()
export class TrackerSettingsEffects extends TrackerService {
  private readonly actions = inject(Actions);
  private readonly wearablesIntegration = inject(WearablesIntegrationService);

  readonly fetchLastPairedTracker = createEffect(
    () => {
      return this.actions.pipe(
        ofType(TrackerSettingsActions.fetchLastPairedTracker),
        switchMap(() => {
          return this.getLastPairedTracker().pipe(
            map((response) => {
              return TrackerSettingsActions.fetchLastPairedTrackerSuccess({
                lastPairedAt: response?.lastPairedAt ?? undefined
              });
            }),
            catchError((error: any) =>
              of(
                TrackerSettingsActions.fetchLastPairedTrackerFailed({
                  error: error?.message
                })
              )
            )
          );
        })
      );
    },
    {
      dispatch: true
    }
  );

  readonly fetchStepsGoalQuery = createEffect(() => {
    return this.actions.pipe(
      ofType(TrackerSettingsActions.fetchStepsGoalQuery),
      switchMap(() =>
        this.findTrackerStepsSetting().pipe(
          map((data) =>
            TrackerSettingsActions.fetchStepsGoalQuerySuccess({ data })
          ),
          catchError((error) =>
            of(
              TrackerSettingsActions.fetchStepsGoalQueryFailed({
                error: error.message
              })
            )
          )
        )
      )
    );
  });

  readonly fetchTrackerGoals = createEffect(() => {
    return this.actions.pipe(
      ofType(TrackerSettingsActions.fetchTrackerGoal),
      mergeMap(({ tracker }) => {
        return this.fetchTrackerGoal().pipe(
          map((response) =>
            TrackerSettingsActions.fetchTrackerGoalSuccess({
              tracker,
              data: {
                goalScore: response ? response.goalScore : undefined,
                goalUnit: response ? response.goalUnit : undefined
              }
            })
          ),
          catchError((error) =>
            of(
              TrackerSettingsActions.fetchTrackerGoalFailed({
                tracker,
                error: error?.message
              })
            )
          )
        );
      })
    );
  });

  readonly fetchUserDisclaimerAgreed = createEffect(
    () => {
      return this.actions.pipe(
        ofType(TrackerSettingsActions.fetchUserDisclaimerAgreed),
        switchMap(() => {
          return this.getUserDisclaimerAgreed().pipe(
            map((response) => {
              return TrackerSettingsActions.fetchUserDisclaimerAgreedSuccess({
                userAgreedAt: response?.userAgreedAt ?? undefined
              });
            }),
            catchError((error: any) =>
              of(
                TrackerSettingsActions.fetchUserDisclaimerAgreedFailed({
                  error: error?.message
                })
              )
            )
          );
        })
      );
    },
    {
      dispatch: true
    }
  );

  readonly fetchUserHealthCategorySettings = createEffect(
    () => {
      return this.actions.pipe(
        ofType(TrackerSettingsActions.fetchUserHealthCategorySettings),
        switchMap(() => {
          return combineLatest({
            healthCategories: this.wearablesIntegration.getCategories({
              limit: 100,
              showOnApp: 'true'
            }),
            healthCategorySetting:
              this.wearablesIntegration.getUserWearableCategories({
                showDefaultCategory: 'true',
                showOnApp: 'true',
                limit: 100,
                onlyShowSupportedCategory: 'true'
              })
          }).pipe(
            map((response) => {
              const list: Array<UserHealthCategorySettings> = [];
              if (response.healthCategories.data) {
                if (
                  response.healthCategorySetting.data &&
                  response.healthCategorySetting.data.results.length > 0
                ) {
                  for (const item of response.healthCategorySetting.data
                    .results) {
                    const category =
                      response.healthCategories.data.results.find(
                        (cat) => cat.id === item.categoryId && cat.showOnApp
                      );
                    if (category) {
                      list.push({
                        ...item,
                        category
                      });
                    }
                  }
                }
              }
              return TrackerSettingsActions.fetchUserHealthCategorySettingsSuccess(
                {
                  data: list
                }
              );
            })
          );
        }),
        catchError((error: any) =>
          of(
            TrackerSettingsActions.fetchUserHealthCategorySettingsFailed({
              error: error?.message
            })
          )
        )
      );
    },
    {
      dispatch: true
    }
  );
}
